// AUTO-GENERATED from packages/core/analytics/events.json. Do not edit.
// Regenerate: npm run analytics:generate

export type AnalyticsEventClass = "intent" | "system";

export interface AnalyticsEventDef {
  readonly name: string;
  readonly eventClass: AnalyticsEventClass;
  readonly surfaces: readonly string[];
  readonly allowedProps: readonly string[];
}

export const FORBIDDEN_PROP_KEYS = ["body", "package_name", "sender", "text", "title"] as const;

export const ANALYTICS_EVENTS = {
  analytics_opt_out_toggled: {
    name: "analytics_opt_out_toggled",
    eventClass: "intent",
    surfaces: ["android", "extension", "web"],
    allowedProps: ["enabled"],
  },
  app_first_open: {
    name: "app_first_open",
    eventClass: "intent",
    surfaces: ["android"],
    allowedProps: [],
  },
  connected_app_registered: {
    name: "connected_app_registered",
    eventClass: "system",
    surfaces: ["server"],
    allowedProps: ["app_kind"],
  },
  daily_usage_rollup: {
    name: "daily_usage_rollup",
    eventClass: "system",
    surfaces: ["android", "extension", "web"],
    allowedProps: [
      "app_foreground_count",
      "app_opens",
      "apps_enabled_count",
      "dismissals_count",
      "glance_viewed_count",
      "mcp_notifications_received_count",
      "notifications_delivered_count",
      "notifications_relayed_count",
      "notifications_seen_count",
      "replies_sent_count",
      "rollup_date",
      "side_panel_opens",
    ],
  },
  extension_installed: {
    name: "extension_installed",
    eventClass: "intent",
    surfaces: ["extension"],
    allowedProps: ["browser", "extension_version"],
  },
  first_dismiss: {
    name: "first_dismiss",
    eventClass: "intent",
    surfaces: ["extension", "web"],
    allowedProps: [],
  },
  first_glance_viewed: {
    name: "first_glance_viewed",
    eventClass: "intent",
    surfaces: ["extension", "web"],
    allowedProps: [],
  },
  first_notification_seen: {
    name: "first_notification_seen",
    eventClass: "intent",
    surfaces: ["extension", "web"],
    allowedProps: ["hours_since_pairing"],
  },
  first_reply_sent: {
    name: "first_reply_sent",
    eventClass: "intent",
    surfaces: ["extension", "web"],
    allowedProps: [],
  },
  glance_download_consented: {
    name: "glance_download_consented",
    eventClass: "intent",
    surfaces: ["extension", "web"],
    allowedProps: [],
  },
  glance_viewed: {
    name: "glance_viewed",
    eventClass: "intent",
    surfaces: ["extension", "web"],
    allowedProps: [],
  },
  mcp_send_notification_called: {
    name: "mcp_send_notification_called",
    eventClass: "system",
    surfaces: ["server"],
    allowedProps: ["app_kind", "rate_limited", "status"],
  },
  onboarding_step_completed: {
    name: "onboarding_step_completed",
    eventClass: "intent",
    surfaces: ["android"],
    allowedProps: ["step"],
  },
  pairing_code_displayed: {
    name: "pairing_code_displayed",
    eventClass: "intent",
    surfaces: ["extension", "web"],
    allowedProps: [],
  },
  pairing_completed: {
    name: "pairing_completed",
    eventClass: "intent",
    surfaces: ["android", "extension", "web"],
    allowedProps: ["time_to_pair_s"],
  },
  pairing_failed: {
    name: "pairing_failed",
    eventClass: "intent",
    surfaces: ["android"],
    allowedProps: ["reason"],
  },
  pairing_started: {
    name: "pairing_started",
    eventClass: "intent",
    surfaces: ["android"],
    allowedProps: [],
  },
  palette_command_executed: {
    name: "palette_command_executed",
    eventClass: "intent",
    surfaces: ["extension", "web"],
    allowedProps: ["command_id"],
  },
  palette_opened: {
    name: "palette_opened",
    eventClass: "intent",
    surfaces: ["extension", "web"],
    allowedProps: [],
  },
  queue_expired_unconsumed: {
    name: "queue_expired_unconsumed",
    eventClass: "system",
    surfaces: ["server"],
    allowedProps: ["device_kind", "expired_count"],
  },
  server_daily_rollup: {
    name: "server_daily_rollup",
    eventClass: "system",
    surfaces: ["server"],
    allowedProps: ["fcm_sent_count", "messages_relayed_count", "sse_delivered_count"],
  },
  side_panel_opened: {
    name: "side_panel_opened",
    eventClass: "intent",
    surfaces: ["extension"],
    allowedProps: [],
  },
  store_cta_clicked: {
    name: "store_cta_clicked",
    eventClass: "intent",
    surfaces: ["landing"],
    allowedProps: ["placement", "store"],
  },
  sync_fallback_served: {
    name: "sync_fallback_served",
    eventClass: "system",
    surfaces: ["server"],
    allowedProps: ["device_kind", "items_bucket"],
  },
  web_app_first_open: {
    name: "web_app_first_open",
    eventClass: "intent",
    surfaces: ["web"],
    allowedProps: [],
  },
  web_app_opened: {
    name: "web_app_opened",
    eventClass: "intent",
    surfaces: ["web"],
    allowedProps: [],
  },
} as const satisfies Record<string, AnalyticsEventDef>;

export type AnalyticsEventName = keyof typeof ANALYTICS_EVENTS;

// Public write-only ingestion key (phc_) — safe to ship in clients by design.
// Personal API keys (phx_) must NEVER appear anywhere in this repo.
export const POSTHOG_HOST = "https://us.i.posthog.com";
export const POSTHOG_PROJECT_KEY = "phc_vecuXfyEQdErsdKdqByoxscsGWMTEARS2CyLXztnxsgR";

export interface SanitizedEvent {
  name: string;
  properties: Record<string, unknown>;
}

/**
 * Builds the only payload shape a platform wrapper may hand to a PostHog SDK.
 * Allowlists properties from the registry (unknown keys are dropped, never
 * sent), strips forbidden keys and `$ip` as defense in depth, and stamps the
 * registry's `event_class` so a call site can never omit or fake it.
 */
export function buildEventPayload(
  event: AnalyticsEventName,
  props: Record<string, unknown> = {}
): SanitizedEvent {
  const def = ANALYTICS_EVENTS[event];
  const properties: Record<string, unknown> = {};
  for (const key of def.allowedProps) {
    const value = props[key];
    if (value !== undefined) properties[key] = value;
  }
  for (const key of FORBIDDEN_PROP_KEYS) delete properties[key];
  delete properties.$ip;
  properties.event_class = def.eventClass;
  return { name: def.name, properties };
}
